import type React from "react";
import {
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
  type RegisterOptions,
  useController,
} from "react-hook-form";

import type { MediaPickerButtonProps } from "@/components/Button/MediaPickerButton";
import { VideoPickerButton } from "@/components/Button/MediaPickerButton";
import type { FormVideo } from "@/components/VideoInput/useVideoInput";

interface FormVideoPickerProps<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
> extends Omit<MediaPickerButtonProps<FormVideo[]>, "onChange" | "value"> {
  name: TName;
  defaultValue?: FieldPathValue<TFormValues, TName>;
  rules?: RegisterOptions<TFormValues, TName>;
  "data-testid"?: string;
}

export function FormVideoPicker<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
>({
  name,
  defaultValue,
  rules,
  "data-testid": dataTestid,
  ...props
}: FormVideoPickerProps<TFormValues, TName>): React.ReactNode {
  const { field, fieldState } = useController<TFormValues, TName>({ name, rules, defaultValue });

  return (
    <VideoPickerButton
      {...props}
      name={name}
      isError={fieldState.invalid}
      onChange={field.onChange}
      value={field.value}
    />
  );
}
